import { axiosInstance } from '../../utils/axios'
import type {
  ApiKey,
  ApiKeyListResponse,
  ApiKeyCreateResponse,
  CreateApiKeyRequest,
  ApiKeyRequest,
  ApiKeyRequestListResponse,
  SubmitApiKeyRequest,
} from '../../types/apiKeys'

const BASE_URL = '/api-keys'

type Envelope<T> = {
  success: boolean
  data?: T
  error?: { code?: string; message?: string }
}

function unwrap<T>(envelope: Envelope<T>): T {
  if (!envelope || envelope.success === false) {
    const err = new Error(envelope?.error?.message || 'API key request failed') as Error & {
      code?: string
    }
    err.code = envelope?.error?.code
    throw err
  }
  return (envelope.data ?? ({} as T)) as T
}

/**
 * Self-serve API key management for the signed-in account.
 *
 * Always sent with the user's Supabase JWT via the axios interceptor — never a
 * partner API key. The backend rejects key-authenticated requests here, because
 * a key that can mint keys survives its own revocation.
 */
export const apiKeysApi = {
  /** List own keys. Previews only; the full key is never re-shown. */
  list: async (): Promise<ApiKeyListResponse> => {
    const res = await axiosInstance.get<Envelope<ApiKeyListResponse>>(BASE_URL)
    return unwrap(res.data)
  },

  /**
   * Generate a key. The plaintext value comes back exactly once — the caller
   * must show it immediately and cannot fetch it again.
   */
  create: async (request: CreateApiKeyRequest = {}): Promise<ApiKeyCreateResponse> => {
    const res = await axiosInstance.post<Envelope<ApiKeyCreateResponse>>(BASE_URL, request)
    return unwrap(res.data)
  },

  /**
   * Delete a key: hides it from the list and revokes it. The record is kept, so
   * its usage history stays available to an admin.
   */
  remove: async (id: string): Promise<ApiKey> => {
    const res = await axiosInstance.post<Envelope<ApiKey>>(`${BASE_URL}/${id}/delete`)
    return unwrap(res.data)
  },

  /** Revoke a key. Takes effect on that key's next request. */
  revoke: async (id: string): Promise<ApiKey> => {
    const res = await axiosInstance.post<Envelope<ApiKey>>(`${BASE_URL}/${id}/revoke`)
    return unwrap(res.data)
  },

  /**
   * Requests for a never-expiring key.
   *
   * Only an admin can mint one, so this submits the ask and reports where it
   * stands. On approval the plaintext goes to the approving admin — a key is
   * shown once, at creation — and reaches the requester out-of-band.
   */
  requests: {
    list: async (): Promise<ApiKeyRequestListResponse> => {
      const res = await axiosInstance.get<Envelope<ApiKeyRequestListResponse>>(
        `${BASE_URL}/requests`
      )
      return unwrap(res.data)
    },

    /** One open request at a time; a second submission is refused. */
    create: async (request: SubmitApiKeyRequest): Promise<ApiKeyRequest> => {
      const res = await axiosInstance.post<Envelope<ApiKeyRequest>>(
        `${BASE_URL}/requests`,
        request
      )
      return unwrap(res.data)
    },

    /** Withdraw while still pending. Distinct from being denied. */
    cancel: async (id: string): Promise<ApiKeyRequest> => {
      const res = await axiosInstance.post<Envelope<ApiKeyRequest>>(
        `${BASE_URL}/requests/${id}/cancel`
      )
      return unwrap(res.data)
    },
  },
}

export default apiKeysApi
